import { useMemo, useRef, useState } from 'react';
import {
  UMAppShell, UMIcon, UMMap, UMMapControls, UMMapToolbar, UMR, UM_RAIL_ITEMS, type UMMapHandle,
} from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import { PlanLogo, fmt } from '../proposition-c/ui';
import type { Bootstrap, Role } from './types';

const LAYERS = [
  {
    id: 'um-buildings', type: 'fill' as const, source: 'buildings',
    paint: {
      'fill-color': ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]] as never,
      'fill-opacity': 0.85,
    },
  },
  { id: 'um-pipes', type: 'line' as const, source: 'pipes', paint: { 'line-color': UMR.map.pipe, 'line-width': UMR.map.pipeWidth } },
  { id: 'um-perimeter', type: 'line' as const, source: 'perimeter', paint: { 'line-color': PLAN.map.perimeter, 'line-width': 2, 'line-dasharray': [2, 1.5] } },
];

/**
 * Step 0 — the user is in UrbanMap, live data, with a perimeter selected.
 * Plan is offered as the next step; the entry point is disabled without the Plan right (PRD US-001).
 */
export function UrbanMapStage({
  data, role, onRoleChange, onCreate, onOpenProjects,
}: {
  data: Bootstrap;
  role: Role;
  onRoleChange: (r: Role) => void;
  onCreate: () => void;
  onOpenProjects: () => void;
}) {
  const mapRef = useRef<UMMapHandle>(null);
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState(data.perimeters[0].label);
  const sources = useMemo(
    () => ({ buildings: data.geo.buildings, pipes: data.geo.pipes, perimeter: data.geo.perimeter }),
    [data.geo],
  );
  const canCreate = role === 'plan';

  return (
    <UMAppShell
      title="UrbanMap"
      withStudioLayout={false}
      railItems={UM_RAIL_ITEMS}
      toolbar={
        <UMMapToolbar
          search={search}
          onSearchChange={setSearch}
          selections={data.perimeters.map((p) => p.label)}
          selectionLabel={folder}
          onSelectionChange={setFolder}
          onCenterSelection={() => mapRef.current?.fitBounds(data.geo.bounds)}
          extra={
            <button
              type="button"
              onClick={canCreate ? onCreate : undefined}
              disabled={!canCreate}
              title={canCreate ? undefined : 'Droit Plan requis'}
              style={{
                pointerEvents: 'auto', height: 49, display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', border: 'none',
                borderLeft: `1px solid ${UMR.border}`, background: canCreate ? PLAN.accent : UMR.border,
                color: canCreate ? UMR.white : UMR.textSecondary, fontFamily: UMR.font, fontSize: 14, fontWeight: 600,
                cursor: canCreate ? 'pointer' : 'not-allowed',
              }}
            >
              <UMIcon name="conversion_path" size={20} color={canCreate ? UMR.white : UMR.textSecondary} />
              Créer un projet Plan
            </button>
          }
        />
      }
    >
      <UMMap ref={mapRef} sources={sources} layers={LAYERS} initialBounds={data.geo.bounds} />
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />

      <aside
        aria-label="Passer à Plan"
        style={{
          position: 'absolute', top: 64, right: 64, width: 352, zIndex: 20, background: UMR.white, fontFamily: UMR.font,
          borderTop: `4px solid ${PLAN.accent}`, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PlanLogo size={36} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Répondre à un appel d’offres</div>
            <div style={{ fontSize: 12, color: UMR.textSecondary }}>
              {folder} · {fmt(data.perimeters.find((p) => p.label === folder)?.buildingCount ?? 0)} bâtiments
            </div>
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: '21px' }}>
          Plan constitue une <strong>copie figée</strong> de ce périmètre pour votre équipe, et recalcule les
          consommations selon les hypothèses du cahier des charges.
        </p>

        {!canCreate && (
          <div style={{ display: 'flex', gap: 10, padding: 10, background: PLAN.warningBg, border: `1px solid ${PLAN.warningBorder}`, color: PLAN.warningText, fontSize: 13 }}>
            <UMIcon name="lock" size={18} color={PLAN.warningText} />
            <div>La création d’un projet nécessite le <strong>droit Plan</strong>. Vous pouvez rejoindre un projet sur invitation.</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={canCreate ? onCreate : undefined}
            disabled={!canCreate}
            style={{
              flex: 1, height: 36, border: 'none', background: canCreate ? PLAN.accent : UMR.border,
              color: canCreate ? UMR.white : UMR.textSecondary, fontFamily: UMR.font, fontWeight: 600, fontSize: 14,
              cursor: canCreate ? 'pointer' : 'not-allowed',
            }}
          >
            Créer un projet Plan
          </button>
          <button
            type="button"
            onClick={onOpenProjects}
            style={{ height: 36, padding: '0 12px', border: `1px solid ${PLAN.accent}`, background: UMR.white, color: PLAN.accentStrong, fontFamily: UMR.font, fontSize: 14, cursor: 'pointer' }}
          >
            Mes projets
          </button>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: UMR.textSecondary, borderTop: `1px solid ${UMR.borderSubtle}`, paddingTop: 10 }}>
          <input type="checkbox" checked={role === 'um-only'} onChange={(e) => onRoleChange(e.target.checked ? 'um-only' : 'plan')} />
          Simuler un utilisateur sans droit Plan
        </label>
      </aside>
    </UMAppShell>
  );
}
